import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BellRing, CalendarCheck, FilePlus2, IndianRupee, Inbox, Luggage, Percent, TrendingUp, Wallet, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { api, fmtDate, inr } from "@/lib/api";
import { ENQ_STATUSES } from "@/lib/constants";
import { Empty, PageHeader, Panel, StatusBadge } from "@/components/admin/common";
import { MonthlyReport } from "@/components/admin/MonthlyReport";
import { sourceLabel } from "@/lib/leads";

const Bars = ({ title, rows, testId }) => {
  const max = Math.max(1, ...rows.map(([, v]) => v));
  return (
    <Panel className="p-5" data-testid={testId}>
      <p className="font-semibold text-navy">{title}</p>
      {rows.length === 0 ? <p className="mt-3 text-sm text-slate-400">No leads yet</p> : (
        <div className="mt-4 space-y-3">{rows.map(([k, v]) => (
          <div key={k}><div className="flex justify-between gap-3 text-sm"><span className="text-slate-600 truncate">{k}</span><span className="font-mono font-semibold text-navy">{v}</span></div>
            <div className="mt-1 h-1.5 rounded-full bg-slate-100"><div className="h-full rounded-full bg-ocean" style={{ width: `${(v / max) * 100}%` }} /></div></div>
        ))}</div>
      )}
    </Panel>
  );
};

const CrmStats = ({ c }) => (
  <div className="mb-6" data-testid="dashboard-crm">
    <div className="grid gap-4 grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
      <Stat icon={Inbox} label="Total leads" value={c.total} accent="bg-sky-50 text-ocean" testId="crm-stat-total" />
      <Stat icon={TrendingUp} label="New leads" value={c.new} accent="bg-cyan-50 text-cyan-700" testId="crm-stat-new" />
      <Stat icon={BellRing} label="Follow-ups due" value={c.follow_ups_due} accent="bg-orange-50 text-orange-600" testId="crm-stat-followups" />
      <Stat icon={FilePlus2} label="Quotations sent" value={c.quoted} accent="bg-sky-50 text-ocean" testId="crm-stat-quoted" />
      <Stat icon={CalendarCheck} label="Confirmed bookings" value={c.confirmed} accent="bg-emerald-50 text-emerald-600" testId="crm-stat-confirmed" />
      <Stat icon={XCircle} label="Lost leads" value={c.lost} accent="bg-slate-100 text-slate-600" testId="crm-stat-lost" />
    </div>
    <div className="mt-4 grid gap-4 lg:grid-cols-2">
      <Bars title="Leads by source" rows={c.by_source.map(([k, v]) => [sourceLabel(k), v])} testId="crm-by-source" />
      <Bars title="Leads by package" rows={c.by_package} testId="crm-by-package" />
    </div>
  </div>
);

const Stat = ({ icon: Icon, label, value, accent, testId }) => (
  <Panel className="p-6">
    <div className="flex items-center justify-between">
      <p className="text-xs uppercase tracking-wider font-semibold text-slate-500">{label}</p>
      <span className={`h-9 w-9 rounded-lg grid place-items-center ${accent}`}><Icon className="h-4 w-4" /></span>
    </div>
    <p className="mt-4 font-mono text-2xl sm:text-3xl font-semibold text-navy" data-testid={testId}>{value}</p>
  </Panel>
);

export default function Dashboard() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setS(r.data)); }, []);

  if (!s) return <div className="grid gap-4 md:grid-cols-4">{[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-32 rounded-2xl" />)}</div>;
  const total = Math.max(s.total_enquiries, 1);

  return (
    <div data-testid="admin-dashboard">
      <PageHeader title="Dashboard" subtitle="Your business at a glance">
        <Button asChild className="bg-ocean hover:bg-navy" data-testid="dashboard-new-quote-button"><Link to="/admin/quotes/new"><FilePlus2 className="h-4 w-4 mr-2" />New quotation</Link></Button>
        <Button asChild variant="outline" data-testid="dashboard-enquiries-button"><Link to="/admin/enquiries">View leads</Link></Button>
      </PageHeader>
      <CrmStats c={s.crm} />
      <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 xl:grid-cols-4">
        <Stat icon={CalendarCheck} label="Active bookings" value={s.active_bookings} accent="bg-sky-50 text-ocean" testId="stat-active-bookings" />
        <Stat icon={IndianRupee} label="Booking revenue" value={inr(s.revenue)} accent="bg-sky-50 text-ocean" testId="stat-revenue" />
        <Stat icon={Wallet} label="Total profit" value={inr(s.profit)} accent="bg-emerald-50 text-emerald-600" testId="stat-profit" />
        <Stat icon={Percent} label="Avg. profit margin" value={`${s.avg_margin}%`} accent="bg-emerald-50 text-emerald-600" testId="stat-margin" />
        <Stat icon={Wallet} label="Balance due" value={inr(s.balance_due)} accent="bg-orange-50 text-coral" testId="stat-balance" />
        <Stat icon={FilePlus2} label="Quotations created" value={s.quotes} accent="bg-sky-50 text-ocean" testId="stat-quotes" />
      </div>

      {s.follow_ups_due > 0 && (
        <Link to="/admin/enquiries?view=followups" data-testid="dashboard-followups-banner"
          className="mt-6 flex items-center gap-4 rounded-2xl border border-orange-200 bg-orange-50 p-5 hover:bg-orange-100 transition-colors">
          <span className="h-10 w-10 shrink-0 rounded-xl bg-white text-orange-600 grid place-items-center"><BellRing className="h-5 w-5" /></span>
          <p className="flex-1 text-sm text-orange-900"><b>{s.follow_ups_due} lead follow-up{s.follow_ups_due === 1 ? "" : "s"}</b> due today or overdue.</p>
          <ArrowRight className="h-4 w-4 text-orange-600 shrink-0" />
        </Link>
      )}

      {s.reminders_due > 0 && (
        <Link to="/admin/reminders" data-testid="dashboard-reminders-banner"
          className="mt-6 flex items-center gap-4 rounded-2xl border border-orange-200 bg-orange-50 p-5 hover:bg-orange-100 transition-colors">
          <span className="h-10 w-10 shrink-0 rounded-xl bg-white text-coral grid place-items-center"><BellRing className="h-5 w-5" /></span>
          <p className="flex-1 text-sm text-orange-900"><b>{s.reminders_due} booking{s.reminders_due === 1 ? "" : "s"}</b> travelling in the next 30 days (or already travelled) still have a balance due.</p>
          <ArrowRight className="h-4 w-4 text-coral shrink-0" />
        </Link>
      )}

      {s.briefings_due > 0 && (
        <Link to="/admin/briefings" data-testid="dashboard-briefings-banner"
          className="mt-6 flex items-center gap-4 rounded-2xl border border-sky-200 bg-sky-50 p-5 hover:bg-sky-100 transition-colors">
          <span className="h-10 w-10 shrink-0 rounded-xl bg-white text-ocean grid place-items-center"><Luggage className="h-5 w-5" /></span>
          <p className="flex-1 text-sm text-sky-900"><b>{s.briefings_due} trip briefing{s.briefings_due === 1 ? "" : "s"}</b> to send: these guests travel within the next 2 days.</p>
          <ArrowRight className="h-4 w-4 text-ocean shrink-0" />
        </Link>
      )}

      <MonthlyReport />

      <div className="mt-6 grid gap-6 grid-cols-1 xl:grid-cols-3">
        <Panel className="p-6 xl:col-span-1">
          <p className="font-semibold text-navy">Enquiry pipeline</p>
          <div className="mt-6 space-y-4">
            {ENQ_STATUSES.map((st) => (
              <div key={st.value} data-testid={`pipeline-${st.value}`}>
                <div className="flex justify-between text-sm"><span className="text-slate-600">{st.label}</span><span className="font-mono font-semibold text-navy">{s.enquiries_by_status[st.value] || 0}</span></div>
                <div className="mt-1.5 h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full bg-ocean" style={{ width: `${((s.enquiries_by_status[st.value] || 0) / total) * 100}%` }} /></div>
              </div>
            ))}
          </div>
        </Panel>
        <Panel className="p-6 xl:col-span-2 min-w-0">
          <div className="flex items-center justify-between">
            <p className="font-semibold text-navy">Recent enquiries</p>
            <Link to="/admin/enquiries" className="text-sm text-ocean font-semibold inline-flex items-center gap-1" data-testid="dashboard-all-enquiries-link">All <ArrowRight className="h-4 w-4" /></Link>
          </div>
          {s.recent_enquiries.length === 0 ? <Empty testId="dashboard-no-enquiries">No enquiries yet. They'll appear here as soon as customers submit the website form.</Empty> : (
            <div className="mt-4 divide-y divide-slate-100">
              {s.recent_enquiries.map((e) => (
                <div key={e.id} className="py-3 flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <p className="font-medium text-navy truncate">{e.name} <span className="text-slate-400 font-normal">· {e.phone}</span></p>
                    <p className="text-xs text-slate-500 truncate">{e.package_name || "No package selected"} · {fmtDate(e.travel_date)} · {e.guests} guests</p>
                  </div>
                  <StatusBadge status={e.status} />
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>

      <Panel className="p-6 mt-6">
        <p className="font-semibold text-navy">Upcoming trips</p>
        {s.upcoming_trips.length === 0 ? <Empty testId="dashboard-no-trips">No upcoming confirmed trips.</Empty> : (
          <div className="mt-4 grid gap-3 grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
            {s.upcoming_trips.map((b) => (
              <div key={b.id} className="rounded-xl bg-mist p-4">
                <p className="font-mono text-xs text-ocean">{fmtDate(b.travel_date)}</p>
                <p className="mt-1 font-semibold text-navy">{b.customer_name}</p>
                <p className="text-xs text-slate-500">{b.package_name} · {b.guests} guests</p>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
}
